import React from 'react';
import PropTypes from 'prop-types';
import { 
  Box, 
  Typography, 
  Button, 
  Drawer, 
  IconButton, 
  Divider, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar, 
  Avatar,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useCart } from '../../contexts/CartContext';
import { Link } from 'react-router-dom';

const Cart = ({ open, onClose }) => {
  const { cart, removeFromCart, updateQuantity, clearCart } = useCart();
  
  // Format price as USD with validation
  const formatPrice = (price) => {
    // Ensure price is a valid number
    const validPrice = typeof price === 'number' && !isNaN(price) ? 
      price : (parseFloat(price) || 0);
      
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(validPrice);
  };
  
  // Handle quantity change
  const handleQuantityChange = (cartItemId, event) => {
    const newQuantity = parseInt(event.target.value);
    if (newQuantity > 0) {
      updateQuantity(cartItemId, newQuantity);
    }
  };
  
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 400 } }
      }}
    >
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
          <ShoppingCartIcon sx={{ mr: 1 }} />
          Your Cart ({cart.itemCount})
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      
      <Divider />
      
      {cart.items.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" gutterBottom>
            Your cart is empty
          </Typography>
          <Button 
            variant="contained" 
            component={Link} 
            to="/products" 
            onClick={onClose}
            sx={{ mt: 2 }}
          >
            Continue Shopping
          </Button>
        </Box>
      ) : (
        <>
          <List sx={{ flexGrow: 1, overflow: 'auto' }}>
            {cart.items.map((item) => (
              <ListItem key={item.cartItemId} divider>
                <ListItemAvatar>
                  <Avatar 
                    variant="rounded" 
                    src={item.images?.[0]?.src || item.images?.[0] || item.image || '/images/placeholder-product.jpg'} 
                    alt={item.name}
                    sx={{ width: 60, height: 60, mr: 1 }}
                    imgProps={{
                      onError: (e) => {
                        e.target.src = '/images/placeholder-product.jpg';
                      }
                    }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={item.name}
                  secondary={
                    <>
                      <Typography variant="body2" component="span">
                        {formatPrice(item.price)} each
                      </Typography>
                      {/* Display options if available */}
                      {typeof item.options === 'string' && item.options.trim() !== '' && (
                        <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                          Options: {item.options}
                        </Typography>
                      )}
                      
                      {/* For legacy format where options is an object */}
                      {typeof item.options === 'object' && Object.keys(item.options).length > 0 && (
                        <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                          Options: {
                            Object.entries(item.options)
                              .filter(([key]) => key !== 'engraving')
                              .map(([key, value]) => {
                                if (typeof value === 'string') return `${key}: ${value}`;
                                if (typeof value === 'object' && value?.title) return `${key}: ${value.title}`;
                                return key;
                              })
                              .join(', ')
                          }
                        </Typography>
                      )}
                      
                      {/* Display engraving if available */}
                      {item.options?.engraving && (
                        <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                          Engraving: {item.options.engraving}
                        </Typography>
                      )}
                      
                      {/* Always display personalization if available */}
                      {item.personalizationText && item.personalizationText.trim() !== '' && (
                        <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                          Personalization: "{item.personalizationText}"
                        </Typography>
                      )}
                      
                      {/* Display custom design indicator if available */}
                      {item.configuratorOptions && (
                        <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                          Custom Design
                        </Typography>
                      )}
                    </>
                  }
                  sx={{ mr: 1 }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <TextField
                    type="number"
                    size="small"
                    value={item.quantity}
                    onChange={(e) => handleQuantityChange(item.cartItemId, e)}
                    inputProps={{ min: 1, style: { textAlign: 'center' } }}
                    sx={{ width: 60, mb: 1 }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {formatPrice(item.price * item.quantity)}
                  </Typography>
                  <IconButton 
                    edge="end" 
                    size="small" 
                    onClick={() => removeFromCart(item.cartItemId)}
                    sx={{ mt: 1 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>
          
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Subtotal:</Typography>
              <Typography variant="h6">{formatPrice(cart.total)}</Typography>
            </Box>
            
            <Button 
              variant="contained" 
              color="primary" 
              fullWidth 
              component={Link}
              to="/checkout"
              onClick={onClose}
              sx={{ mb: 1 }}
            >
              Checkout
            </Button>
            
            <Button 
              variant="outlined" 
              color="primary" 
              fullWidth
              onClick={onClose}
              component={Link}
              to="/products"
              sx={{ mb: 1 }}
            >
              Continue Shopping
            </Button>
            
            <Button 
              variant="text" 
              color="error" 
              onClick={clearCart}
              sx={{ display: 'block', mx: 'auto', mt: 1 }}
            >
              Clear Cart
            </Button>
          </Box>
        </>
      )}
    </Drawer>
  );
};

Cart.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default Cart;
